// Admin UI client для notify-go internal endpoints.
// Изолирован от services/api.ts (другой агент рефакторит admin-ui — не трогаем общий api.ts).
//
// Все методы здесь требуют admin_token (стандартный bearer admin'а).
// Bearer JWT прикрепляется интерсептором ниже — notify-go проверяет его через auth-go и валидирует роль admin.

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api'; // SEC/joblex: same-origin relative base

const adminNotify = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

adminNotify.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('admin_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export interface AdminNotificationTemplate {
  template_key: string;
  event_type?: string | null;
  title_tpl: string;
  body_tpl: string;
  icon?: string | null;
  link_tpl?: string | null;
  severity: 'info' | 'success' | 'warning' | 'error';
  default_channels: string[];
  enabled: boolean;
  audience_type: 'user' | 'all' | 'admin' | 'segment';
  updated_at: string;
}

export interface AdminCreateNotificationPayload {
  template_key?: string;
  title: string;
  body: string;
  icon?: string;
  link?: string;
  severity?: 'info' | 'success' | 'warning' | 'error';
  audience_type: 'user' | 'all' | 'admin' | 'segment';
  audience_filter?: Record<string, unknown>;
  channels?: string[];
  scheduled_at?: string;
}

// Direct to notify-go via nginx /api/admin/notifications/ — admin role check happens in notify-go middleware.
const PREFIX = '/admin/notifications';

export const adminNotificationsAPI = {
  async listTemplates(): Promise<AdminNotificationTemplate[]> {
    const { data } = await adminNotify.get<{ items: AdminNotificationTemplate[] }>('/admin/notification-templates');
    return data.items ?? [];
  },

  async upsertTemplate(t: AdminNotificationTemplate): Promise<void> {
    await adminNotify.put('/admin/notification-templates', t);
  },

  async create(payload: AdminCreateNotificationPayload): Promise<{ id: number }> {
    const { data } = await adminNotify.post<{ id: number }>(PREFIX, payload);
    return data;
  },

  async previewAudience(audienceType: string, audienceFilter?: Record<string, unknown>): Promise<number> {
    const { data } = await adminNotify.post<{ count: number }>(`${PREFIX}/preview-audience`, {
      audience_type: audienceType,
      audience_filter: audienceFilter,
    });
    return data.count ?? 0;
  },

  async listPending(): Promise<unknown[]> {
    const { data } = await adminNotify.get<{ pending: unknown[] }>(PREFIX);
    return data.pending ?? [];
  },
};
